<template>
    <!-- The Group Reports tab: every group report for this report, as a table.
         Click a column heading to sort by it; the search box matches report
         and group names on the server. -->
    <div class="flex w-full flex-col gap-4">
        <div class="relative w-full max-w-sm">
            <tabler-icon    name="search"
                            size-class="h-4 w-4"
                            class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            ></tabler-icon>
            <input  type="search"
                    :value="search"
                    placeholder="Search group reports"
                    aria-label="Search group reports"
                    class="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    @input="$emit('update:search', $event.target.value)"
            >
        </div>

        <div class="w-full overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table class="min-w-full text-left">
                <thead class="bg-gray-50">
                    <tr>
                        <th v-for="column in COLUMNS"
                            :key="column.key"
                            scope="col"
                            class="px-4 py-3 text-sm font-normal text-gray-500"
                            :aria-sort="ariaSort(column.key)"
                        >
                            <button type="button"
                                    class="inline-flex items-center gap-1.5 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    @click="sortBy(column.key)"
                            >
                                {{ column.label }}
                                <tabler-icon    :name="sortIcon(column.key)"
                                                size-class="h-4 w-4"
                                ></tabler-icon>
                            </button>
                        </th>
                        <th scope="col"
                            class="w-12 px-4 py-3"
                        ><span class="sr-only">Actions</span></th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-200">
                    <tr v-for="template in rows"
                        :key="template.id"
                        class="even:bg-gray-50"
                    >
                        <td class="px-4 py-3 text-sm">
                            <a  v-if="template.canView"
                                :href="editUrl(template)"
                                class="text-blue-600 hover:text-blue-800 hover:underline"
                            >{{ template.name }}</a>
                            <span   v-else
                                    class="text-gray-700"
                            >{{ template.name }}</span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900">{{ template.groupName }}</td>
                        <td class="px-4 py-3 text-sm">
                            <frequency-badge    :interval="template.interval"
                                                :weekday="template.weekday"
                            ></frequency-badge>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex flex-wrap gap-1.5">
                                <span   v-for="chip in chips(template)"
                                        :key="chip"
                                        class="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                                >{{ chip }}</span>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-right">
                            <action-menu    v-if="template.canView && menuItems(template).length > 0"
                                            :items="menuItems(template)"
                                            :label="`Actions for ${template.name}`"
                            ></action-menu>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div    v-if="loading && templates.length === 0"
                    class="flex items-center justify-center gap-2 px-4 py-10 text-sm text-gray-500"
            >
                <tabler-icon    name="loader"
                                size-class="h-4 w-4 animate-spin"
                ></tabler-icon>
                Loading group reports
            </div>

            <div    v-else-if="templates.length === 0"
                    class="flex flex-col items-center gap-3 px-4 py-12 text-center"
            >
                <tabler-icon    name="inbox"
                                size-class="h-8 w-8"
                                class="text-gray-400"
                ></tabler-icon>
                <p class="m-0 text-base font-medium text-gray-900">
                    {{ search ? 'No group reports match your search' : 'No group reports yet' }}
                </p>
                <a  v-if="canCreate && ! search"
                    :href="newUrl"
                    class="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 hover:text-white hover:no-underline"
                >New Group Report</a>
            </div>
        </div>
    </div>
</template>

<script>
import TablerIcon from "@/components/elements/TablerIcon.vue"
import ActionMenu from "@/components/elements/ActionMenu.vue"
import FrequencyBadge from "./FrequencyBadge.vue"
import {enabledFeatureLabels} from "./enabledFeatures.js"
import {sortTemplates} from "./groupReportRows.js"

const COLUMNS = [
    {key: 'name', label: 'Name'},
    {key: 'group', label: 'Group'},
    {key: 'frequency', label: 'Frequency'},
    {key: 'includes', label: 'Includes'},
]

export default {
    name: "GroupReportList",
    components: {TablerIcon, ActionMenu, FrequencyBadge},
    emits: ['update:search', 'run', 'history', 'archive'],
    props: {
        templates: {
            type: Array,
            required: true,
        },
        schema: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        search: {
            type: String,
            default: '',
        },
        // (template) => URL of its Edit page.
        editUrl: {
            type: Function,
            required: true,
        },
        newUrl: {
            type: String,
            required: true,
        },
        canCreate: {
            type: Boolean,
            default: false,
        },
        canRun: {
            type: Boolean,
            default: false,
        },
        canArchive: {
            type: Boolean,
            default: false,
        },
        canViewHistory: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            COLUMNS,
            sortKey: null,
            sortDirection: 'asc',
        }
    },
    computed: {
        rows() {
            return sortTemplates(this.templates, this.schema, this.sortKey, this.sortDirection)
        },
    },
    methods: {
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                return
            }
            this.sortKey = key
            this.sortDirection = 'asc'
        },
        sortIcon(key) {
            if (this.sortKey !== key) {
                return 'selector'
            }
            return this.sortDirection === 'asc' ? 'chevronUp' : 'chevronDown'
        },
        ariaSort(key) {
            if (this.sortKey !== key) {
                return 'none'
            }
            return this.sortDirection === 'asc' ? 'ascending' : 'descending'
        },
        chips(template) {
            return enabledFeatureLabels(this.schema, template.settings)
        },
        menuItems(template) {
            return [
                {label: 'Edit', icon: 'pencil', onSelect: () => window.location.assign(this.editUrl(template))},
                this.canRun && {label: 'Run for Me', icon: 'user', onSelect: () => this.$emit('run', {template, forGroup: false})},
                this.canRun && {label: 'Run for Group', icon: 'users', onSelect: () => this.$emit('run', {template, forGroup: true})},
                this.canViewHistory && {label: 'History', icon: 'history', onSelect: () => this.$emit('history', template)},
                this.canArchive && {label: 'Archive', icon: 'archive', danger: true, onSelect: () => this.$emit('archive', template)},
            ].filter(Boolean)
        },
    },
}
</script>
